import type { ElementType, ReactNode } from "react";
import type { ContainerProps } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import {
  AppBar,
  Box,
  Container,
  IconButton,
  Stack,
  Toolbar,
  Typography,
  alpha,
  styled,
} from "@mui/material";
import { useRouter } from "next/router";

import { APPBAR_HEIGHT, PAGE_PADDING } from "@/constants/layout";
import { Z_INDEX } from "@/constants/zIndex";
import { useAppSelector } from "@/store";
import AppMenu from "./AppMenu";

// The original's `_app` wraps every page in `<main style="overflow:hidden;
// height:100%">`, so pages scroll INSIDE their own container rather than
// scrolling the document. This root is that container: without it, `top:` on a
// sticky child resolves against a scroll port that never scrolls and the
// sticky layers silently degrade to static.
const Root = styled(Box, {
  shouldForwardProp: (prop) => prop !== "ownsScroll",
})<{ ownsScroll?: boolean }>(({ theme, ownsScroll }) => ({
  height: "100%",
  // A page either scrolls in this root (the simple centred-column routes) or
  // hands the scrolling to a child that fills it (the menu, whose left column
  // is its own scroll port). Getting this wrong is what breaks `position:
  // sticky`: a sticky element only sticks inside its nearest scrolling
  // ancestor, and an `overflow: hidden` box in between makes THAT the
  // reference — so the bar scrolls away with the content it was pinned to.
  overflowY: ownsScroll ? "hidden" : "auto",
  // `clip` (not `hidden`) so sticky positioning still works.
  overflowX: "clip",
  display: "flex",
  flexDirection: "column",
  minHeight: 0,
  backgroundColor: theme.palette.background.default,
}));

const Bar = styled(AppBar)(({ theme }) => ({
  height: APPBAR_HEIGHT,
  justifyContent: "center",
  backgroundColor: theme.palette.background.paper,
  color: theme.palette.text.primary,
  boxShadow: `0 1px 0 ${alpha(theme.palette.divider, 0.3)}`,
  zIndex: Z_INDEX.NAVBAR,
}));

const Body = styled(Container)<{ component?: ElementType }>(({ theme }) => ({
  flex: 1,
  // Flex items default to `min-height: auto`, which lets this grow past the
  // shell and hands the scrolling back to an ancestor. Zero keeps it exactly
  // as tall as the shell so a child can own the scroll.
  minHeight: 0,
  paddingTop: theme.spacing(2),
  paddingBottom: theme.spacing(4),
  paddingLeft: PAGE_PADDING,
  paddingRight: PAGE_PADDING,
}));

/** App bar + centred content column shared by every inner page. */
interface PageShellProps {
  title?: string;
  subtitle?: string;
  action?: ReactNode;
  children?: ReactNode;
  maxWidth?: ContainerProps["maxWidth"];
  hideBack?: boolean;
  hideMenu?: boolean;
  /**
   * Drop the app bar entirely. The original's own `PageShell` is nothing but a
   * row container — the menu page brings its own `HomeNavbar` and no route has
   * a back-arrow/title bar — so this is how a page opts out of the chrome this
   * reconstruction added.
   */
  hideBar?: boolean;
  /** Remove the body's own padding, for pages that manage their own gutters. */
  disableBodyPadding?: boolean;
}

export default function PageShell({
  title,
  subtitle,
  action,
  children,
  maxWidth = "sm",
  hideBack,
  hideMenu,
  hideBar,
  disableBodyPadding,
}: PageShellProps) {
  const router = useRouter();
  const businessName = useAppSelector(
    (state) => state.settings.data.public_business_name,
  );

  return (
    <Root ownsScroll={hideBar}>
      {!hideBar && (
      <Bar position="sticky" elevation={0}>
        {/* The bar's BACKGROUND is full-bleed but its CONTENTS align with the
            page's content column, exactly as the original does (its header is
            1175px wide inside a full-width main). Without this the title sat
            hard against the window edge while everything below was inset. */}
        <Toolbar
          sx={{
            minHeight: `${APPBAR_HEIGHT}px !important`,
            gap: 1,
            width: "100%",
            // Custom breakpoints are declared in types/mui.d.ts, so this indexes cleanly.
            maxWidth: (theme) =>
              maxWidth ? theme.breakpoints.values[maxWidth] : "none",
            mx: "auto",
            px: `${PAGE_PADDING}px`,
          }}
        >
          {!hideBack && (
            <IconButton
              edge="start"
              aria-label="Go back"
              onClick={() => router.back()}
              sx={{ width: 44, height: 44 }}
            >
              <ArrowBackIcon />
            </IconButton>
          )}
          <Stack sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              component="h1"
              variant="subtitle1"
              noWrap
              fontWeight={700}
            >
              {title || businessName}
            </Typography>
            {subtitle && (
              <Typography variant="caption" color="text.secondary" noWrap>
                {subtitle}
              </Typography>
            )}
          </Stack>
          {action}
          {!hideMenu && <AppMenu />}
        </Toolbar>
      </Bar>
      )}
      {/* Not `component="main"`: `_app` already provides the page's single
          `<main>` landmark, exactly as the original does. */}
      <Body
        maxWidth={maxWidth}
        disableGutters
        sx={
          disableBodyPadding
            ? { p: 0, display: "flex", flexDirection: "column" }
            : undefined
        }
      >
        {children}
      </Body>
    </Root>
  );
}
